// ── AI Configure from Scope — shared types + seeding hook ─────────────────────
//
// A rep on Account Setup > Existing Customer can ask an LLM to read this
// customer's DealHub Scope record (their SOW/pre-sales scoping data) and
// propose pre-built configuration for every in-scope Platform Journey stage —
// mirroring Sumsub's "AI agents build compliance setup" capability. Nothing
// here is ever auto-applied: every suggestion is reviewed and explicitly
// applied by the rep, stage by stage, the same way they'd configure it by hand.
import { useEffect, useRef, useState } from 'react'
import type {
  AmlScreeningConfig, DuplicateRuleConfig, IdvProvider, KybOwnershipConfig, TmRuleConfig,
} from './types'

export type AiConfidence = 'high' | 'medium' | 'low'

// ── Optimization objectives — read before any per-stage suggestion is drafted,
// same principle as "ask about outcomes, not implementation": what is this
// customer actually trying to achieve (pass rate, cost, speed, low friction,
// stronger fraud/compliance posture, or a balance), and where do they operate
// — rather than jumping straight to which vendor/cascade to configure. A rep
// reviews and can edit this before it's used to draft anything.
export type AiOptimizationObjective =
  | 'pass_rate' | 'cost' | 'speed_time_to_market' | 'latency'
  | 'fraud_protection' | 'regulatory_compliance' | 'balanced'

export const AI_OBJECTIVE_LABELS: Record<AiOptimizationObjective, string> = {
  pass_rate: 'Highest pass rate',
  cost: 'Lowest cost',
  speed_time_to_market: 'Speed / time to market',
  latency: 'Lowest latency',
  fraud_protection: 'Strongest fraud controls',
  regulatory_compliance: 'Strongest compliance posture',
  balanced: 'Balanced — no single priority',
}

export const ALL_AI_OPTIMIZATION_OBJECTIVES: AiOptimizationObjective[] = [
  'pass_rate', 'cost', 'speed_time_to_market', 'latency', 'fraud_protection', 'regulatory_compliance', 'balanced',
]

export interface AiObjectives {
  primary: AiOptimizationObjective
  /** Optional — null when there's genuinely nothing to suggest a second priority. */
  secondary: AiOptimizationObjective | null
  /** Every market/jurisdiction this customer operates in, as free-text labels
   * (e.g. "Australia", "United Kingdom") — not a fixed enum, since a customer's
   * real footprint can be anything and shouldn't be constrained to a picklist. */
  jurisdictions: string[]
  /** General regulatory/compliance considerations for those jurisdictions —
   * general compliance knowledge, not a claim about FrankieOne's own product
   * coverage in that market. */
  regulatoryNotes: string
  rationale: string
  confidence: AiConfidence
  /** True only when the Scope record's own wording explicitly names this
   * priority; false when it's the model's recommendation, reasoned from
   * surrounding context (industry, jurisdiction, volume, use-case wording)
   * rather than a stated fact. The model always makes a real recommendation
   * either way — this only tells the rep which kind it's looking at. */
  stated: boolean
}

// References an id from the platform's own DEFAULT_* catalog (cascade check
// ids, fraud signal ids, KYB AU check ids, risk factor ids) — the AI only
// decides which of the platform's known items are enabled, it never invents
// new catalog entries, so labels/descriptions never drift from what's real.
export interface AiIdSuggestion {
  id: string
  enabled: boolean
  /** Only meaningful for risk factors, where the AI can also propose a weight. */
  weight?: number
}

export interface AiIdvSuggestion {
  defaultProvider: IdvProvider
  autoRunKyc: boolean
  rationale: string
  confidence: AiConfidence
}

export interface AiCascadeSuggestion {
  checks: AiIdSuggestion[]
  rationale: string
  confidence: AiConfidence
}

export interface AiDuplicatesSuggestion {
  customRules: DuplicateRuleConfig[]
  rationale: string
  confidence: AiConfidence
}

export interface AiRiskSuggestion {
  riskFactors: AiIdSuggestion[]
  rationale: string
  confidence: AiConfidence
}

export interface AiKybSuggestion {
  auChecks: AiIdSuggestion[]
  ownership?: Partial<KybOwnershipConfig>
  rationale: string
  confidence: AiConfidence
}

export interface AiAmlSuggestion {
  config: AmlScreeningConfig
  rationale: string
  confidence: AiConfidence
}

export interface AiFraudSuggestion {
  signals: AiIdSuggestion[]
  rationale: string
  confidence: AiConfidence
}

export interface AiTmSuggestion {
  config: TmRuleConfig
  rationale: string
  confidence: AiConfidence
}

// recipeKey references one of Scope's own V1_RECIPES (@features/scope/types) by
// its `key` — deliberately typed as a plain string here rather than importing
// V1RecipeKey, since it can also be null (the AI says "not applicable", e.g.
// this customer is on Platform v2, where recipes don't apply at all).
export interface AiWorkflowSuggestion {
  recipeKey: string | null
  rationale: string
  confidence: AiConfidence
}

export interface AiConfigureSuggestions {
  generatedAt: string
  idv?: AiIdvSuggestion
  cascade?: AiCascadeSuggestion
  duplicates?: AiDuplicatesSuggestion
  risk?: AiRiskSuggestion
  kyb?: AiKybSuggestion
  aml?: AiAmlSuggestion
  fraud?: AiFraudSuggestion
  tm?: AiTmSuggestion
  workflow?: AiWorkflowSuggestion
}

export type AiStageKey = keyof Omit<AiConfigureSuggestions, 'generatedAt'>

// Every key AiConfigureSuggestions can carry, as a plain array — lets index.tsx
// loop over "all 9 stages" (e.g. to bump every stage's token together on a
// fresh batch) without hand-maintaining a second list in sync with the type.
export const ALL_AI_STAGE_KEYS: AiStageKey[] = [
  'idv', 'cascade', 'duplicates', 'risk', 'kyb', 'aml', 'fraud', 'tm', 'workflow',
]

// ── AI Configure chat — a follow-up conversation about an already-generated
// batch of suggestions ("why did you include X?", "don't suggest AML, they
// already have it", "bump the PEP weight"). Same human-review gate as the
// suggestions themselves: the model can only ever propose adjustments to the
// *suggestion*, never write real config — a rep still explicitly applies each
// stage's config themselves, same as before this conversation existed.
export interface AiChatMessage {
  role: 'user' | 'assistant'
  content: string
}

// Toggles a stage in/out of the checklist — the same effect as clicking its
// checkbox by hand.
export interface AiChatSetIncludedAction {
  type: 'set_included'
  stage: AiStageKey
  included: boolean
}

// A full replacement of one stage's suggestion object (not a partial patch —
// the chat endpoint is instructed to always return the complete shape, same
// as configure-from-scope would for that stage). Applying this also re-opens
// that one stage for review, even if it had already been applied or
// dismissed, without touching any other stage's state.
export interface AiChatUpdateSuggestionAction {
  type: 'update_suggestion'
  stage: AiStageKey
  suggestion: unknown
}

export type AiChatAction = AiChatSetIncludedAction | AiChatUpdateSuggestionAction

export interface AiChatResponse {
  reply: string
  actions: AiChatAction[]
}

// Merges an id-keyed suggestion (enabled + optional weight) onto the
// platform's own default catalog — anything the AI didn't mention keeps its
// current value, and labels/descriptions always come from the real catalog,
// never from the AI's response.
export function applyIdSuggestions<T extends { id: string; enabled: boolean; weight?: number }>(
  defaults: T[],
  suggestions: AiIdSuggestion[],
): T[] {
  const bySugg = new Map(suggestions.map((s) => [s.id, s]))
  return defaults.map((item) => {
    const s = bySugg.get(item.id)
    if (!s) return item
    return { ...item, enabled: s.enabled, ...(typeof s.weight === 'number' ? { weight: s.weight } : {}) }
  })
}

export interface AiSeed<T> {
  /** Must change (e.g. increment) every time a new suggestion should be
   * (re-)applied — value alone isn't enough, since a rep might revert a
   * suggestion by hand and then want to re-apply the same one. */
  token: number
  value: T
}

// The shared "apply an externally-authored suggestion into this panel's own
// local state" pattern — mirrors the seed useEffect TransactionMonitoringStage
// already used for `journeyRecord.tmConfig`, generalised for every panel.
export function useAiSeed<T>(seed: AiSeed<T> | null | undefined, apply: (value: T) => void) {
  const lastToken = useRef<number | null>(null)
  useEffect(() => {
    if (!seed || seed.token === lastToken.current) return
    lastToken.current = seed.token
    apply(seed.value)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seed])
}

export interface AiStageSuggestionState<T> {
  /** Pass straight through to the panel's aiSeed prop — null until the rep
   * explicitly applies the suggestion, so nothing is ever seeded silently. */
  seed: AiSeed<T> | null
  applied: boolean
  /** A suggestion exists for this stage and hasn't been applied or dismissed yet. */
  pending: boolean
  /** True once the rep has explicitly dismissed this batch's suggestion. */
  dismissed: boolean
  /** Whether the banner should be shown at all — a suggestion exists and
   * hasn't been dismissed (still true once applied, so the "Applied" state
   * and its Re-apply link stay visible). */
  visible: boolean
  apply: () => void
  dismiss: () => void
  /** Bidirectional version of dismiss/undo-dismiss — backs a checklist
   * (checked = included, unchecked = dismissed) rather than a one-way
   * Dismiss button. Re-including after a dismiss just clears `dismissed`;
   * it doesn't un-apply an already-applied suggestion. */
  setIncluded: (included: boolean) => void
}

// The human-review gate: a fresh suggestion never seeds a panel by itself —
// it just becomes "pending" (shown via AiSuggestionBanner) until the rep
// clicks Apply. Generating a brand new batch (seedToken changes) always
// re-opens review, even if a previous batch for this stage was applied or
// dismissed already.
export function useAiStageSuggestion<T>(suggestion: T | undefined, seedToken: number): AiStageSuggestionState<T> {
  const [seenToken, setSeenToken] = useState(seedToken)
  const [dismissed, setDismissed] = useState(false)
  const [appliedToken, setAppliedToken] = useState(0)
  if (seedToken !== seenToken) {
    setSeenToken(seedToken)
    setDismissed(false)
    setAppliedToken(0)
  }
  const applied = appliedToken !== 0 && appliedToken === seedToken
  const pending = !!suggestion && !dismissed && !applied
  return {
    seed: applied && suggestion ? { token: appliedToken, value: suggestion } : null,
    applied,
    pending,
    dismissed,
    visible: !!suggestion && !dismissed,
    apply: () => setAppliedToken(seedToken),
    dismiss: () => setDismissed(true),
    setIncluded: (included) => setDismissed(!included),
  }
}
